import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "@/lib/api";
import { money } from "@/lib/format";
import { Alert, Badge, Button, Card, EmptyState, Field, PageHeader, Skeleton } from "@/components/ui";

export interface ServiceLevel {
  id: string;
  code: string;
  name: string;
  description?: string;
  base_fee_cents: number;
  per_km_cents: number;
  min_price_cents: number;
  max_weight_g?: number;
  collection_cutoff?: string;
  disable_after_cutoff: boolean;
  collection_lead_days: number;
  delivery_days_min: number;
  delivery_days_max: number;
  collection_window_start?: string;
  collection_window_end?: string;
  delivery_window_start?: string;
  delivery_window_end?: string;
  active: boolean;
  sort_order?: number;
}

interface FormState {
  code: string;
  name: string;
  description: string;
  base_fee: string; // Rands, as typed
  per_km: string;
  min_price: string;
  max_weight_kg: string;
  collection_cutoff: string;
  disable_after_cutoff: boolean;
  collection_lead_days: string;
  delivery_days_min: string;
  delivery_days_max: string;
  collection_window_start: string;
  collection_window_end: string;
  delivery_window_start: string;
  delivery_window_end: string;
  active: boolean;
}

const EMPTY_FORM: FormState = {
  code: "",
  name: "",
  description: "",
  base_fee: "",
  per_km: "",
  min_price: "",
  max_weight_kg: "",
  collection_cutoff: "",
  disable_after_cutoff: false,
  collection_lead_days: "0",
  delivery_days_min: "1",
  delivery_days_max: "1",
  collection_window_start: "08:00",
  collection_window_end: "16:00",
  delivery_window_start: "09:00",
  delivery_window_end: "18:00",
  active: true,
};

function toForm(level: ServiceLevel): FormState {
  return {
    code: level.code,
    name: level.name,
    description: level.description ?? "",
    base_fee: (level.base_fee_cents / 100).toString(),
    per_km: (level.per_km_cents / 100).toString(),
    min_price: (level.min_price_cents / 100).toString(),
    max_weight_kg: level.max_weight_g ? (level.max_weight_g / 1000).toString() : "",
    collection_cutoff: level.collection_cutoff ?? "",
    disable_after_cutoff: level.disable_after_cutoff,
    collection_lead_days: String(level.collection_lead_days),
    delivery_days_min: String(level.delivery_days_min),
    delivery_days_max: String(level.delivery_days_max),
    collection_window_start: level.collection_window_start ?? "",
    collection_window_end: level.collection_window_end ?? "",
    delivery_window_start: level.delivery_window_start ?? "",
    delivery_window_end: level.delivery_window_end ?? "",
    active: level.active,
  };
}

function toRequest(form: FormState) {
  return {
    code: form.code.trim().toUpperCase(),
    name: form.name.trim(),
    description: form.description.trim(),
    base_fee_cents: Math.round(Number(form.base_fee || 0) * 100),
    per_km_cents: Math.round(Number(form.per_km || 0) * 100),
    min_price_cents: Math.round(Number(form.min_price || 0) * 100),
    max_weight_g: form.max_weight_kg ? Math.round(Number(form.max_weight_kg) * 1000) : 0,
    collection_cutoff: form.collection_cutoff.trim(),
    disable_after_cutoff: form.disable_after_cutoff,
    collection_lead_days: Math.round(Number(form.collection_lead_days || 0)),
    delivery_days_min: Math.round(Number(form.delivery_days_min || 0)),
    delivery_days_max: Math.round(Number(form.delivery_days_max || 0)),
    collection_window_start: form.collection_window_start.trim(),
    collection_window_end: form.collection_window_end.trim(),
    delivery_window_start: form.delivery_window_start.trim(),
    delivery_window_end: form.delivery_window_end.trim(),
    active: form.active,
  };
}

// How a level's rules read back as a sentence, so an admin can see what they
// have actually promised without working it out from five numbers.
function scheduleSummary(level: ServiceLevel): string {
  const collect =
    level.collection_lead_days === 0
      ? "Collects same day"
      : level.collection_lead_days === 1
        ? "Collects next working day"
        : `Collects in ${level.collection_lead_days} working days`;

  const deliver =
    level.delivery_days_min === 0 && level.delivery_days_max === 0
      ? "delivers same day"
      : level.delivery_days_min === level.delivery_days_max
        ? `delivers ${level.delivery_days_min} working day${level.delivery_days_min === 1 ? "" : "s"} later`
        : `delivers ${level.delivery_days_min}-${level.delivery_days_max} working days later`;

  const cutoff = level.collection_cutoff
    ? `, cutoff ${level.collection_cutoff}${level.disable_after_cutoff ? " (off after cutoff)" : " (rolls to next day)"}`
    : "";

  return `${collect}, ${deliver}${cutoff}`;
}

/**
 * What WeShip sells and how a route prices: base fee + a per-km rate against
 * the road distance between collection and delivery (see
 * internal/courier/fleet.go). No deploy needed -- a change here reaches the
 * next quote within the fleet provider's 30-second cache window.
 */
export function ServiceLevels() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["service-levels"],
    queryFn: () => api.get<{ data: ServiceLevel[] }>("/v1/admin/rates/service-levels"),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["service-levels"] });

  const create = useMutation({
    mutationFn: () => api.post<ServiceLevel>("/v1/admin/rates/service-levels", toRequest(form)),
    onSuccess: () => {
      setForm(EMPTY_FORM);
      setError("");
      invalidate();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Could not create the service level."),
  });

  const update = useMutation({
    mutationFn: () => api.put<ServiceLevel>(`/v1/admin/rates/service-levels/${editingId}`, toRequest(form)),
    onSuccess: () => {
      setForm(EMPTY_FORM);
      setEditingId(null);
      setError("");
      invalidate();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Could not update the service level."),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.remove<void>(`/v1/admin/rates/service-levels/${id}`),
    onSuccess: invalidate,
  });

  function startEdit(level: ServiceLevel) {
    setEditingId(level.id);
    setForm(toForm(level));
    setError("");
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError("");
  }

  const saving = create.isPending || update.isPending;
  const canSave = form.code.trim() && form.name.trim();

  return (
    <>
      <PageHeader
        title="Service levels"
        subtitle="What customers can book, and the formula that prices it: base fee + per-km rate * road distance."
      />

      <Card className="mb-5">
        <h2 className="mb-4 text-lg font-semibold tracking-tight">{editingId ? "Edit service level" : "Add a service level"}</h2>
        {error && (
          <div className="mb-4">
            <Alert>{error}</Alert>
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Code"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
            placeholder="ODM"
            hint="Shown to the customer as the option's identifier."
          />
          <Field
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="On Demand"
          />
          <Field
            label="Description (optional)"
            className="sm:col-span-2"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Collected and delivered the same day."
          />
          <Field
            label="Base fee (R)"
            type="number"
            min={0}
            step="0.01"
            value={form.base_fee}
            onChange={(e) => setForm((f) => ({ ...f, base_fee: e.target.value }))}
          />
          <Field
            label="Per km (R)"
            type="number"
            min={0}
            step="0.01"
            value={form.per_km}
            onChange={(e) => setForm((f) => ({ ...f, per_km: e.target.value }))}
            hint="Set from your fuel cost and vehicle consumption."
          />
          <Field
            label="Minimum price (R)"
            type="number"
            min={0}
            step="0.01"
            value={form.min_price}
            onChange={(e) => setForm((f) => ({ ...f, min_price: e.target.value }))}
            hint="Never quote below this, however short the route."
          />
          <Field
            label="Max weight (kg, optional)"
            type="number"
            min={0}
            value={form.max_weight_kg}
            onChange={(e) => setForm((f) => ({ ...f, max_weight_kg: e.target.value }))}
            hint="Leave blank for no limit."
          />
          <label className="flex items-center gap-2 self-end pb-2.5 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand"
              checked={form.active}
              onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
            />
            Active -- sellable on the next quote
          </label>
        </div>

        <h3 className="mb-1 mt-6 text-sm font-semibold tracking-tight">Scheduling</h3>
        <p className="mb-4 text-xs text-ink-400">
          What the customer is offered on the booking form: which days this level can collect, what it promises for
          delivery, and the hours each leg may be asked for. Everything counts in working days.
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field
            label="Booking cutoff (optional)"
            value={form.collection_cutoff}
            onChange={(e) => setForm((f) => ({ ...f, collection_cutoff: e.target.value }))}
            placeholder="11:00"
            hint="HH:MM. Past this, see the box below."
          />
          <label className="flex items-start gap-2 self-end pb-2.5 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-ink-300 text-brand"
              checked={form.disable_after_cutoff}
              onChange={(e) => setForm((f) => ({ ...f, disable_after_cutoff: e.target.checked }))}
            />
            <span>
              Stop offering after the cutoff
              <span className="block text-xs text-ink-400">
                On for same-day: it cannot roll to tomorrow and still be same-day. Off, and the earliest collection
                simply moves to the next working day.
              </span>
            </span>
          </label>
          <Field
            label="Collection lead (working days)"
            type="number"
            min={0}
            value={form.collection_lead_days}
            onChange={(e) => setForm((f) => ({ ...f, collection_lead_days: e.target.value }))}
            hint="0 collects today, 1 the next working day."
          />
          <Field
            label="Delivery from (working days)"
            type="number"
            min={0}
            value={form.delivery_days_min}
            onChange={(e) => setForm((f) => ({ ...f, delivery_days_min: e.target.value }))}
            hint="Counted from collection. 0 is same day."
          />
          <Field
            label="Delivery to (working days)"
            type="number"
            min={0}
            value={form.delivery_days_max}
            onChange={(e) => setForm((f) => ({ ...f, delivery_days_max: e.target.value }))}
            hint="Same as the minimum for a fixed promise."
          />
          <div />
          <Field
            label="Collection window opens"
            value={form.collection_window_start}
            onChange={(e) => setForm((f) => ({ ...f, collection_window_start: e.target.value }))}
            placeholder="08:00"
          />
          <Field
            label="Collection window closes"
            value={form.collection_window_end}
            onChange={(e) => setForm((f) => ({ ...f, collection_window_end: e.target.value }))}
            placeholder="16:00"
          />
          <div />
          <Field
            label="Delivery window opens"
            value={form.delivery_window_start}
            onChange={(e) => setForm((f) => ({ ...f, delivery_window_start: e.target.value }))}
            placeholder="09:00"
          />
          <Field
            label="Delivery window closes"
            value={form.delivery_window_end}
            onChange={(e) => setForm((f) => ({ ...f, delivery_window_end: e.target.value }))}
            placeholder="18:00"
          />
        </div>
        <div className="mt-4 flex gap-2">
          <Button
            loading={saving}
            disabled={!canSave}
            onClick={() => (editingId ? update.mutate() : create.mutate())}
          >
            {editingId ? "Save changes" : "Add service level"}
          </Button>
          {editingId && (
            <Button variant="secondary" onClick={cancelEdit}>
              Cancel
            </Button>
          )}
        </div>
      </Card>

      {isLoading ? (
        <Card>
          <Skeleton className="h-32 w-full" />
        </Card>
      ) : !data?.data.length ? (
        <EmptyState title="No service levels yet" body="Add one above -- nothing quotes until at least one is active." />
      ) : (
        <div className="ws-card divide-y divide-ink-200">
          {data.data.map((level) => (
            <div key={level.id} className="flex flex-wrap items-center gap-3 px-5 py-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-mono text-xs font-medium text-ink-500">{level.code}</p>
                  <p className="font-semibold">{level.name}</p>
                  {!level.active && <Badge tone="bad">inactive</Badge>}
                </div>
                {level.description && <p className="mt-0.5 text-sm text-ink-500">{level.description}</p>}
                <p className="mt-1 text-xs text-ink-400">
                  {money(level.base_fee_cents)} base + {money(level.per_km_cents)}/km, min {money(level.min_price_cents)}
                  {level.max_weight_g ? ` -- up to ${(level.max_weight_g / 1000).toFixed(1)} kg` : ""}
                </p>
                <p className="mt-0.5 text-xs text-ink-400">{scheduleSummary(level)}</p>
              </div>
              <Button size="sm" variant="secondary" onClick={() => startEdit(level)}>
                Edit
              </Button>
              <Button size="sm" variant="danger" onClick={() => remove.mutate(level.id)}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
